import styled from '@emotion/styled';
import { ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useNavigate } from 'react-router';
import { routes } from '@navigation/routes';
import { ProjectType } from '@pages/types';
import { useAppTranslation } from '@hooks';
import { TxKeyPath } from '@i18n';
import {
  Avatar,
  Col,
  colors,
  HoverCard,
  Icon,
  IAvailableIcons,
  IHoverCardProps,
  Row,
  Space,
  Text,
} from 'common-components';

type CardProjectPropsType = IHoverCardProps & {
  project: ProjectType
};

const ContentContainer = styled(Col)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
`;
const FooterContainer = styled(Row)`
  width: 100%;
  height: 56px;
`;
const HoverContentContainer = styled(Row)`
  width: 100%;
`;
const StatesRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  padding: 0 20px;
`;
const StateColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const StateLabel = styled.span<{ color: string; weight: number }>`
  font-size: 9px;
  line-height: 13px;
  font-weight: ${({ weight }) => weight};
  color: ${({ color }) => color};
  white-space: nowrap;
`;

const CardProject = function ({ project, ...rest }: CardProjectPropsType) {
  const navigate = useNavigate();
  const { translate } = useAppTranslation();

  const data = [
    {
      name: 'Page A',
      uv: 4000,
      pv: 2400,
      amt: 2400,
    },
    {
      name: 'Page B',
      uv: 3000,
      pv: 1398,
      amt: 2210,
    },
    {
      name: 'Page C',
      uv: 2000,
      pv: 9800,
      amt: 2290,
    },
    {
      name: 'Page D',
      uv: 2780,
      pv: 3908,
      amt: 2000,
    },
    {
      name: 'Page E',
      uv: 1890,
      pv: 4800,
      amt: 2181,
    },
    {
      name: 'Page F',
      uv: 2390,
      pv: 3800,
      amt: 2500,
    },
    {
      name: 'Page G',
      uv: 3490,
      pv: 4300,
      amt: 2100,
    },
  ];

  const states = [
    { state: 20, size: 49, color: colors.lightBlue500, halo: colors.lightBlue100, haloOpacity: 1 },
    { state: 30, size: 27, color: colors.success500, halo: colors.success100, haloOpacity: 0.5 },
    { state: 80, size: 19, color: colors.warning500, halo: colors.warning100, haloOpacity: 0.5 },
    { state: 90, size: 9, color: colors.error500, halo: colors.error100, haloOpacity: 0 },
  ];

  const footerItems: { icon: IAvailableIcons; value: string | number; onClick?: () => void }[] = [
    { icon: 'FileQuestion02', value: project.nbTasks },
    { icon: 'Users03', value: project.nbActiveSupplier },
    { icon: 'AlertHexagon', value: project.nbAlerts },
  ];

  const hoverFooterItems: typeof footerItems = [
    {
      icon: 'ClockStopwatch',
      value: project.spendTime,
      onClick: () => navigate(routes.projectDashboard.navigate(project.idProject)),
    },
    {
      icon: 'FileQuestion02',
      value: project.nbTasks,
      onClick: () => navigate(routes.projectTasks.navigate(project.idProject)),
    },
    {
      icon: 'Users03',
      value: project.nbActiveSupplier,
      onClick: () => navigate(routes.projectAudience.navigate(project.idProject)),
    },
    { icon: 'AlertHexagon', value: project.nbAlerts },
  ];

  const name = (
    <Text size="md" color={colors.gray900} style={{ fontWeight: 500 }}>
      {project.name}
    </Text>
  );

  return (
    <HoverCard
      style={{ width: '100%' }}
      ContentComponent={
        <ContentContainer>
          {name}
          <Text size="xs" color={colors.gray400}>
            {translate('form.updatedOn', { date: project.updateDate })}
          </Text>
          <ResponsiveContainer width="100%" height={49}>
            <BarChart
              data={[...data, ...data, ...data]}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              barCategoryGap={2.5}
            >
              <Bar dataKey="uv" fill={colors.purple400} strokeOpacity={0} radius={10} />
            </BarChart>
          </ResponsiveContainer>
        </ContentContainer>
      }
      footerContent={
        <FooterContainer align={'middle'} justify={'space-between'}>
          {/*FIXME: no avatar data*/}
          <Avatar size="xs" />
          <Space size={11}>
            {footerItems.map((item) => (
              <Space key={item.icon} size={8}>
                <Icon icon={item.icon} color={colors.gray400} size={20} />
                <Text size="xs" color={colors.gray600}>
                  {item.value}
                </Text>
              </Space>
            ))}
          </Space>
        </FooterContainer>
      }
      hoverContent={
        <ContentContainer style={{ width: '100%' }}>
          {name}
          <StatesRow>
            {states.map(({ state, size, color, halo, haloOpacity }) => (
              <StateColumn key={state}>
                <svg width={size} height={size}>
                  <circle cx={size / 2} cy={size / 2} r={size / 2} fill={halo} opacity={haloOpacity} />
                  <circle cx={size / 2} cy={size / 2} r={4.5} fill={color} />
                </svg>
                <StateLabel color={colors.gray700} weight={500}>
                  {translate(`common.states.taskState.${state}` as TxKeyPath)}
                </StateLabel>
                <StateLabel color={colors.gray500} weight={400}>
                  1921
                </StateLabel>
              </StateColumn>
            ))}
          </StatesRow>
        </ContentContainer>
      }
      hoverFooterContent={
        <HoverContentContainer align={'middle'} justify={'center'}>
          <Space size={11}>
            {hoverFooterItems.map((item) => (
              <Space
                key={item.icon}
                size={8}
                style={{ cursor: item.onClick ? 'pointer' : 'default' }}
                onClick={item.onClick}
              >
                <Icon icon={item.icon} color={colors.white} size={20} />
                <Text color={colors.white} size="xs">
                  {item.value}
                </Text>
              </Space>
            ))}
          </Space>
        </HoverContentContainer>
      }
      {...rest}
    />
  );
};

export default CardProject;
